import { useState, useMemo, useCallback } from 'react';
import { TreePine, Scissors, Sparkles, RotateCcw, Plus, Trash2 } from 'lucide-react';
import { type AppState, type Branch, type BranchStatus } from './types';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { type ToolItem } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import { PruneBranchLive } from '../../components/prioritization/session/liveExperience/liveTools/PruneBranchLive';

function PruneProductTreeContent(props: ToolHostProps) {
  const { isLiveSession, shouldAutoStart } = useLiveToolSession();
  
  const [state, setState] = useState<AppState>('input');
  const [branches, setBranches] = useState<Branch[]>([]);
  const [newBranchName, setNewBranchName] = useState('');
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  
  // Track source IDs for imported items
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});

  // Handler for importing items from workspace
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newBranches: Branch[] = toolItems.map((item) => ({
      id: item.id,
      name: item.name,
      status: 'active' as BranchStatus,
      value: 3,
      impact: 3,
      feasibility: 3,
    }));
    
    // Track source IDs
    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach(item => {
      if (item.sourceId) {
        newSourceIdMap[item.id] = item.sourceId;
      }
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));
    
    setBranches(prev => [...prev, ...newBranches]);
    if (shouldAutoStart && newBranches.length > 0) {
      setSelectedBranch(newBranches[0].id);
      setState('tree');
    }
  }, [shouldAutoStart]);
  
  // Calculate branch score
  const calculateScore = (branch: Branch): number => {
    return (branch.value + branch.impact + branch.feasibility) / 3;
  };
  
  // Get results for saving as session result
  const handleGetResults = useCallback((): ToolResultItem[] => {
    // Kept branches first, then pruned ones, each by score (highest first).
    // A pruned branch is an answer ("prune it"), not a missing one: the
    // counting rule groups it as Prune; leaving it out dropped it from the result.
    const byScore = (a: Branch, b: Branch) => calculateScore(b) - calculateScore(a);
    const orderedBranches = [
      ...branches.filter(b => b.status === 'active').sort(byScore),
      ...branches.filter(b => b.status === 'pruned').sort(byScore),
    ];

    return orderedBranches.map((branch, idx) => ({
      id: sourceIdMap[branch.id] || branch.id,
      name: branch.name,
      rank: idx + 1,
      score: calculateScore(branch),
      category: branch.status,
      metadata: {
        value: branch.value,
        impact: branch.impact,
        feasibility: branch.feasibility,
        status: branch.status,
      }
    }));
  }, [branches, sourceIdMap]);

  // Initialize tree with branches
  const handleStart = () => {
    if (branches.length === 0) {
      alert('Please add at least one branch to start.');
      return;
    }
    setState('tree');
  };

  // Add a new branch
  const handleAddBranch = () => {
    if (!newBranchName.trim()) return;

    const newBranch: Branch = {
      id: `branch-${Date.now()}`,
      name: newBranchName.trim(),
      status: 'active',
      value: 3,
      impact: 3,
      feasibility: 3,
    };

    setBranches([...branches, newBranch]);
    setNewBranchName('');
  };

  // Update branch metrics
  const handleUpdateBranch = (id: string, field: 'value' | 'impact' | 'feasibility', value: number) => {
    setBranches((prev) =>
      prev.map((branch) =>
        branch.id === id ? { ...branch, [field]: value } : branch
      )
    );
  };

  // Toggle branch status (prune/unprune)
  const handleToggleBranch = (id: string) => {
    setBranches((prev) =>
      prev.map((branch) =>
        branch.id === id
          ? { ...branch, status: branch.status === 'active' ? 'pruned' : 'active' }
          : branch
      )
    );
  };

  // Delete a branch
  const handleDeleteBranch = (id: string) => {
    setBranches((prev) => prev.filter((branch) => branch.id !== id));
  };

  const canSubmitLive = isLiveSession && branches.length > 0 && state === 'tree';

  // Get active and pruned branches
  const activeBranches = useMemo(() => {
    return branches.filter((b) => b.status === 'active').sort((a, b) => calculateScore(b) - calculateScore(a));
  }, [branches]);

  const prunedBranches = useMemo(() => {
    return branches.filter((b) => b.status === 'pruned');
  }, [branches]);

  // Reset
  const handleReset = () => {
    setState('input');
    setBranches([]);
    setSelectedBranch(null);
    setSourceIdMap({});
  };

  return (
    <ToolWrapper
      toolName="Prune Product Tree"
      toolDescription="Prune branches that don't add value and nurture those that drive outcomes"
      onImportItems={handleImportItems}
      itemCount={branches.length}
      onGetResults={canSubmitLive || state === 'tree' ? handleGetResults : undefined}
      {...props}
    >
      {isLiveSession && state === 'tree' && branches.length > 0 && (
        <LiveToolScaffold
          title="Prune the product tree"
          subtitle="Rate branches and prune what doesn't earn its place"
        >
          <PruneBranchLive
            branches={branches}
            selectedId={selectedBranch}
            onSelect={setSelectedBranch}
            onTogglePrune={handleToggleBranch}
            onMetricChange={handleUpdateBranch}
          />
        </LiveToolScaffold>
      )}

      {!isLiveSession && (
      <div className="w-full max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 pb-4 border-b border-[var(--rule)]">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <TreePine className="h-8 w-8 text-[var(--green)] flex-shrink-0" />
              <div>
                <h1 className="text-2xl font-bold text-[var(--text-pri)]">
                  Prune the Product Tree
                </h1>
                <p className="text-sm text-[var(--text-sec)] mt-1">
                  Visualize priorities as branches on a tree. Prune branches that don't add value and nurture those that drive outcomes.
                </p>
              </div>
            </div>
            {state !== 'input' && (
              <button
                onClick={handleReset}
                className="text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] px-3 py-1 rounded hover:bg-[var(--hover-tint)] whitespace-nowrap flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div>
          {state === 'input' && (
            <div className="space-y-6">
              {/* Show imported items if any */}
              {branches.length > 0 && branches.some(b => sourceIdMap[b.id]) && (
                <div className="p-4 bg-[var(--green-dim)] border border-[var(--green)]">
                  <h3 className="font-medium text-[var(--green)] dark:text-[var(--text-ter)] mb-2">
                    {branches.filter(b => sourceIdMap[b.id]).length} Items Imported as Branches
                  </h3>
                  <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                    You can rate and prune these branches after starting.
                  </p>
                </div>
              )}
              
              <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
                <h3 className="font-semibold text-[var(--text-sec)] mb-2">How to Use</h3>
                <ul className="text-sm text-[var(--text-sec)] space-y-1 list-disc list-inside">
                  <li>Add features or priorities as branches on your product tree</li>
                  <li>Once started, you'll rate each branch on value, impact, and feasibility</li>
                  <li>Prune (remove) branches that don't add enough value</li>
                  <li>Nurture (keep) branches that drive outcomes</li>
                </ul>
              </div>

              <div className="space-y-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newBranchName}
                    onChange={(e) => setNewBranchName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddBranch()}
                    placeholder="Enter a feature or priority..."
                    className="flex-1 px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <button
                    onClick={handleAddBranch}
                    className="px-4 py-2 bg-[var(--green)] text-white hover:bg-[var(--green)] flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Branch
                  </button>
                </div>

                {branches.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-[var(--text-pri)]">Branches ({branches.length})</h3>
                    <div className="space-y-2">
                      {branches.map((branch) => (
                        <div
                          key={branch.id}
                          className="flex items-center justify-between p-3 bg-[var(--bg-raised)] border border-[var(--rule)]"
                        >
                          <span className="text-[var(--text-pri)]">{branch.name}</span>
                          <button
                            onClick={() => handleDeleteBranch(branch.id)}
                            className="text-[var(--coral)] hover:text-[var(--coral)] p-1"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleStart}
                  disabled={branches.length === 0}
                  className="w-full px-6 py-3 bg-[var(--green)] text-white hover:bg-[var(--green)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed font-semibold"
                >
                  Start Pruning
                </button>
              </div>
            </div>
          )}

          {state === 'tree' && (
            <div className="space-y-6">
              {/* Tree Visualization */}
              <div className="bg-gradient-to-b from-green-50 to-white border-2 border-[var(--green)] p-8 min-h-[400px]">
                <div className="flex items-center justify-center mb-6">
                  <div className="text-center">
                    <TreePine className="h-16 w-16 text-[var(--green)] mx-auto mb-2" />
                    <h2 className="text-xl font-bold text-[var(--text-pri)]">Product Tree</h2>
                  </div>
                </div>

                {/* Active Branches */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-[var(--text-pri)] flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-[var(--green)]" />
                    Active Branches ({activeBranches.length})
                  </h3>
                  {activeBranches.length === 0 ? (
                    <p className="text-[var(--text-ter)] text-center py-8">No active branches. Add branches to get started.</p>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {activeBranches.map((branch) => (
                        <div
                          key={branch.id}
                          className={`p-4  border-2 transition-all ${
                            selectedBranch === branch.id
                              ? 'border-[var(--green)] bg-[var(--green-dim)]'
                              : 'border-[var(--green)] bg-[var(--bg-raised)] hover:border-[var(--green)]'
                          }`}
                          onClick={() => setSelectedBranch(branch.id === selectedBranch ? null : branch.id)}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <h4 className="font-semibold text-[var(--text-pri)] flex-1">{branch.name}</h4>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleBranch(branch.id);
                              }}
                              className="text-[var(--coral)] hover:text-[var(--coral)] p-1"
                              title="Prune this branch"
                            >
                              <Scissors className="h-5 w-5" />
                            </button>
                          </div>

                          {selectedBranch === branch.id && (
                            <div className="space-y-3 mt-3 pt-3 border-t border-[var(--rule)]">
                              <div>
                                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                                  Value (1-5)
                                </label>
                                <input
                                  type="range"
                                  min="1"
                                  max="5"
                                  value={branch.value}
                                  onChange={(e) =>
                                    handleUpdateBranch(branch.id, 'value', parseInt(e.target.value))
                                  }
                                  className="w-full"
                                />
                                <div className="flex justify-between text-xs text-[var(--text-ter)]">
                                  <span>Low</span>
                                  <span className="font-semibold">{branch.value}</span>
                                  <span>High</span>
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                                  Impact (1-5)
                                </label>
                                <input
                                  type="range"
                                  min="1"
                                  max="5"
                                  value={branch.impact}
                                  onChange={(e) =>
                                    handleUpdateBranch(branch.id, 'impact', parseInt(e.target.value))
                                  }
                                  className="w-full"
                                />
                                <div className="flex justify-between text-xs text-[var(--text-ter)]">
                                  <span>Low</span>
                                  <span className="font-semibold">{branch.impact}</span>
                                  <span>High</span>
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                                  Feasibility (1-5)
                                </label>
                                <input
                                  type="range"
                                  min="1"
                                  max="5"
                                  value={branch.feasibility}
                                  onChange={(e) =>
                                    handleUpdateBranch(branch.id, 'feasibility', parseInt(e.target.value))
                                  }
                                  className="w-full"
                                />
                                <div className="flex justify-between text-xs text-[var(--text-ter)]">
                                  <span>Low</span>
                                  <span className="font-semibold">{branch.feasibility}</span>
                                  <span>High</span>
                                </div>
                              </div>

                              <div className="pt-2 border-t border-[var(--rule)]">
                                <div className="text-sm">
                                  <span className="text-[var(--text-sec)]">Score: </span>
                                  <span className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
                                    {calculateScore(branch).toFixed(1)}/5.0
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pruned Branches */}
                {prunedBranches.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-[var(--rule)]">
                    <h3 className="font-semibold text-[var(--text-sec)] flex items-center gap-2 mb-4">
                      <Scissors className="h-5 w-5 text-[var(--coral)]" />
                      Pruned Branches ({prunedBranches.length})
                    </h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      {prunedBranches.map((branch) => (
                        <div
                          key={branch.id}
                          className="p-3 bg-[var(--hover-tint)] border border-[var(--rule)] opacity-60"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[var(--text-sec)] line-through">{branch.name}</span>
                            <button
                              onClick={() => handleToggleBranch(branch.id)}
                              className="text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] p-1"
                              title="Restore this branch"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
                <h3 className="font-semibold text-[var(--text-pri)] mb-4">Summary</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-[var(--green-dim)]">
                    <div className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{activeBranches.length}</div>
                    <div className="text-sm text-[var(--text-sec)]">Active Branches</div>
                  </div>
                  <div className="text-center p-4 bg-[var(--coral-dim)]">
                    <div className="text-2xl font-bold text-[var(--coral)]">{prunedBranches.length}</div>
                    <div className="text-sm text-[var(--text-sec)]">Pruned Branches</div>
                  </div>
                  <div className="text-center p-4 bg-[var(--slate-dim)]">
                    <div className="text-2xl font-bold text-[var(--text-sec)]">{branches.length}</div>
                    <div className="text-sm text-[var(--text-sec)]">Total Branches</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      )}
    </ToolWrapper>
  );
}

export default function PruneProductTree(props: ToolHostProps) {
  return <PruneProductTreeContent {...props} />;
}
